// src/components/tasks/TasksOverviewCard.tsx
// Stats dashboard card showing Due, Completed, Points, Streak
import React, { useMemo } from 'react';
import { IonIcon, IonProgressBar } from '@ionic/react';
import { flame } from 'ionicons/icons';
import { Card } from '../ui/Card';
import type { TaskAssignment, FamilyMember } from '../../types';
import './tasks.css';

export interface TasksOverviewCardProps {
  assignments: TaskAssignment[];
  familyMembers: FamilyMember[];
  currentUserId?: string;
  filter: 'today' | 'week' | 'all';
}

// Helper to get today's date string
const toDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const TasksOverviewCard: React.FC<TasksOverviewCardProps> = ({
  assignments,
  familyMembers,
  currentUserId,
  filter,
}) => {
  // Calculate stats
  const stats = useMemo(() => {
    const today = toDateString(new Date());

    // Filter based on current view
    // For 'today' filter, include both today's tasks AND overdue incomplete tasks
    let filtered = assignments;
    if (filter === 'today') {
      filtered = assignments.filter(a =>
        a.due_date === today || (a.due_date < today && !a.is_completed)
      );
    }

    const total = filtered.length;
    const completed = filtered.filter(a => a.is_completed).length;
    const remaining = total - completed;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Total points earned (all time from assignments shown)
    const pointsEarned = assignments
      .filter(a => a.is_completed)
      .reduce((sum, a) => sum + (a.task?.points_value || 0), 0);

    // Get current user's streak
    const currentMember = familyMembers.find(m => m.user_id === currentUserId);
    const streak = currentMember?.current_streak || 0;

    // Count overdue tasks specifically for the label
    const overdueCount = assignments.filter(a => a.due_date < today && !a.is_completed).length;

    return { remaining, completed, total, percentage, pointsEarned, streak, overdueCount };
  }, [assignments, familyMembers, currentUserId, filter]);

  return (
    <Card title="Overview" size="md">
      <div className="tasks-overview-card">
        {/* Stats Grid */}
        <div className="tasks-overview-stats">
          <div className="tasks-overview-stat">
            <div className="tasks-overview-stat__value tasks-overview-stat__value--primary">
              {stats.remaining}
            </div>
            <div className="tasks-overview-stat__label">
              {filter === 'today'
                ? (stats.overdueCount > 0 ? `Due (${stats.overdueCount} overdue)` : 'Due Today')
                : 'Remaining'}
            </div>
          </div>

          <div className="tasks-overview-stat">
            <div className="tasks-overview-stat__value tasks-overview-stat__value--success">
              {stats.completed}/{stats.total}
            </div>
            <div className="tasks-overview-stat__label">Completed</div>
          </div>

          <div className="tasks-overview-stat">
            <div className="tasks-overview-stat__value tasks-overview-stat__value--info">
              {stats.pointsEarned}
            </div>
            <div className="tasks-overview-stat__label">Points</div>
          </div>

          {stats.streak > 0 && (
            <div className="tasks-overview-stat">
              <div className="tasks-overview-stat__value" style={{ color: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <IonIcon icon={flame} />
                {stats.streak}
              </div>
              <div className="tasks-overview-stat__label">Streak</div>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        {stats.total > 0 && (
          <div className="tasks-overview-progress">
            <div className="tasks-overview-progress__bar">
              <div
                className="tasks-overview-progress__fill"
                style={{ width: `${stats.percentage}%` }}
              />
            </div>
            <div className="tasks-overview-progress__label">
              {stats.percentage}% complete
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

TasksOverviewCard.displayName = 'TasksOverviewCard';

export default TasksOverviewCard;
